'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'

/**
 * 0.6 single-section practice tests. Starts an RW-only or Math-only SAT (that
 * section's 2 modules, no break) via /api/sat/start with `sectionScope`. A near-
 * copy of StartSatButton; kept separate so the heavily-used full-SAT button
 * stays untouched. Pro-gated server-side (the API returns 403 for free users).
 */
export default function StartSatSectionButton({
  sectionScope,
  variant = 'outline',
  children,
}: {
  sectionScope: 'RW' | 'MATH'
  variant?: 'default' | 'outline' | 'secondary'
  children: React.ReactNode
}) {
  const router = useRouter()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div>
      <Button
        variant={variant}
        disabled={busy}
        onClick={async () => {
          try {
            setBusy(true)
            setError(null)
            const r = await fetch('/api/sat/start', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sectionScope }),
            })
            const data = await r.json().catch(() => ({}))
            if (!r.ok) {
              if (r.status === 403) {
                setError(data.error || 'Single-section tests are a Pro feature.')
              } else {
                setError(data.error || 'Failed to start test. Please try again.')
              }
              return
            }
            router.push(data?.goto ?? '/sat/resume')
          } catch {
            setError('Network error. Please try again.')
          } finally {
            setBusy(false)
          }
        }}
      >
        {busy ? 'Starting…' : children}
      </Button>
      {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
    </div>
  )
}
